"use client";

import React from "react";
import { Activity, TriangleAlert, CircleCheck, Clock, Server, Zap } from "lucide-react";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
export default function LegacyVsModern() {
  const legacyMetrics = [{
    label: "Costi Acqua Annuali",
    value: "€12.400",
    status: "poor"
  }, {
    label: "Ore Lavoro Irrigazione",
    value: "450h",
    status: "poor"
  }, {
    label: "Salute Pianta (Indice)",
    value: "62/100",
    status: "poor"
  }];
  const modernMetrics = [{
    label: "Costi Acqua Annuali",
    value: "€6.200",
    status: "good"
  }, {
    label: "Ore Lavoro Irrigazione",
    value: "110h",
    status: "good"
  }, {
    label: "Salute Pianta (Indice)",
    value: "94/100",
    status: "good"
  }];
  return <section data-section-id="263786" id="analisi-del-risparmio" className="bg-background text-foreground relative overflow-hidden py-24 sm:py-32">
      {}
      <style dangerouslySetInnerHTML={{
      __html: `
          @keyframes scanline {
            0% { transform: translateX(-100%); opacity: 0; }
            10% { opacity: 1; }
            90% { opacity: 1; }
            100% { transform: translateX(200%); opacity: 0; }
          }
          .animate-scanline {
            animation: scanline 8s ease-in-out infinite;
          }
          .jitter-text {
            text-shadow: 1px 0 0 rgba(239,68,68,0.2), -1px 0 0 rgba(91,127,166,0.2);
          }
        `
    }} />

      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-3xl font-bold tracking-tight sm:text-4xl md:text-5xl font-default mb-4">
            Metodi Tradizionali vs. <span className="text-secondary">Tecnologia Solid Green</span>
          </h2>
          <p className="text-lg text-muted-foreground font-default">
            Analisi comparativa del ROI: scopri come la nostra tecnologia trasforma l'efficienza idrica e la produttività del tuo terreno agricolo.
          </p>
        </div>

        <div className="relative grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-stretch">
          
          {}
          <div className="absolute top-0 bottom-0 left-0 w-full pointer-events-none z-50 overflow-hidden rounded-xl hidden lg:block">
            <div className="h-full w-1 bg-secondary/80 animate-scanline absolute left-0 top-0" />
          </div>

          {}
          <Card className="relative overflow-hidden border-border/50 bg-muted/10 grayscale-[0.8] hover:grayscale-0 transition-all duration-500 flex flex-col rounded-xl shadow-md">
            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAiIGhlaWdodD0iMjAiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+PGNpcmNsZSBjeD0iMiIgY3k9IjIiIHI9IjIiIGZpbGw9IiMzMzMiIGZpbGwtb3BhY2l0eT0iMC4xIi8+PC9zdmc+')] opacity-20 mix-blend-overlay pointer-events-none" />
            
            <CardHeader className="pb-4 border-b border-border/30">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <Server className="w-5 h-5" />
                  <span className="font-mono text-sm uppercase tracking-wider">Metodo Convenzionale</span>
                </div>
                <Badge variant="outline" className="bg-destructive/10 text-destructive border-destructive/20 font-mono">
                  <Clock className="w-3 h-3 mr-1" />
                  Alto Spreco
                </Badge>
              </div>
              <CardTitle className="text-2xl font-default jitter-text text-muted-foreground">Gestione Tradizionale</CardTitle>
              <CardDescription className="font-mono text-xs">Modello basato su irrigazione standard</CardDescription>
            </CardHeader>

            <CardContent className="pt-6 flex-grow">
              {}
              <div className="space-y-4 opacity-70">
                <div className="h-8 w-3/4 bg-muted rounded-md animate-pulse" />
                <div className="h-32 w-full bg-muted rounded-md flex items-center justify-center border border-border/50">
                  <TriangleAlert className="w-8 h-8 text-destructive/50" />
                </div>
                <div className="space-y-2">
                  <div className="h-4 w-full bg-muted rounded-md" />
                  <div className="h-4 w-5/6 bg-muted rounded-md" />
                  <div className="h-4 w-4/6 bg-muted rounded-md" />
                </div>
                <div className="h-10 w-32 bg-muted rounded-md mt-4" />
              </div>
            </CardContent>

            <CardFooter className="bg-muted/20 border-t border-border/30 flex-col items-start gap-3 py-4">
              <div className="w-full text-xs font-mono text-muted-foreground uppercase tracking-wider mb-2">Metriche di Performance</div>
              <div className="w-full space-y-2">
                {legacyMetrics.map((metric, i) => <div key={i} data-index={i} className="flex justify-between items-center w-full">
                    <span className="text-muted-foreground/80 font-mono text-xs">{metric.label}</span>
                    <span className="text-destructive font-mono text-sm">{metric.value}</span>
                  </div>)}
              </div>
            </CardFooter>
          </Card>

          {}
          <Card className="relative overflow-hidden border-secondary/30 bg-card flex flex-col rounded-xl shadow-lg">
            <div className="absolute top-0 right-0 -mt-16 -mr-16 w-64 h-64 bg-secondary/10 rounded-lg blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 -mb-16 -ml-16 w-64 h-64 bg-primary/10 rounded-lg blur-3xl pointer-events-none" />
            
            <CardHeader className="pb-4 border-b border-border/50 relative z-10">
              <div className="flex justify-between items-start mb-2">
                <div className="flex items-center gap-2 text-secondary">
                  <Zap className="w-5 h-5" />
                  <span className="font-mono text-sm uppercase tracking-wider font-semibold">Solid Green Italia</span>
                </div>
                <Badge variant="outline" className="bg-secondary/10 text-secondary border-secondary/20 font-mono">
                  <Activity className="w-3 h-3 mr-1" />
                  -50% Acqua
                </Badge>
              </div>
              <CardTitle className="text-2xl font-default text-foreground">Agricoltura Rigenerativa</CardTitle>
              <CardDescription className="font-mono text-xs text-secondary/70">Ottimizzato con Solid Dew RSL</CardDescription>
            </CardHeader>

            <CardContent className="pt-6 flex-grow relative z-10">
              {}
              <div className="space-y-4">
                <div className="h-8 w-3/4 bg-gradient-to-r from-secondary/20 to-transparent rounded-md" />
                <div className="h-32 w-full bg-gradient-to-br from-accent to-muted rounded-lg flex items-center justify-center border border-border/50 shadow-md relative overflow-hidden">
                   <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full animate-[shimmer_2s_infinite]" />
                   <CircleCheck className="w-8 h-8 text-secondary/70" />
                </div>
                <div className="space-y-2">
                  <div className="h-4 w-full bg-muted/50 rounded-md" />
                  <div className="h-4 w-5/6 bg-muted/50 rounded-md" />
                  <div className="h-4 w-4/6 bg-muted/50 rounded-md" />
                </div>
                <div className="h-10 w-32 bg-secondary/20 text-secondary rounded-md mt-4 flex items-center justify-center text-sm font-medium border border-secondary/30">
                  Efficienza Massima
                </div>
              </div>
            </CardContent>

            <CardFooter className="bg-secondary/5 border-t border-secondary/10 flex-col items-start gap-3 py-4 relative z-10">
              <div className="w-full text-xs font-mono text-secondary/80 uppercase tracking-wider mb-2">Metriche di Performance</div>
              <div className="w-full space-y-2">
                {modernMetrics.map((metric, i) => <div key={i} data-index={i} className="flex justify-between items-center w-full">
                    <span className="text-muted-foreground font-mono text-xs">{metric.label}</span>
                    <span className="text-secondary font-mono text-sm font-semibold">{metric.value}</span>
                  </div>)}
              </div>
            </CardFooter>
          </Card>

        </div>

        <div className="mt-16 text-center">
          <Button size="lg" className="font-default rounded-lg px-8 bg-secondary hover:bg-secondary/90 text-secondary-foreground shadow-md transition-all">
            Calcola il Tuo Risparmio
            <Zap className="ml-2 w-4 h-4" />
          </Button>
        </div>
      </div>
    </section>;
}